import { memo, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getMilageByStopsInSuccess } from '@redux/shipment/ShipmentActions.js';
import { useGetQuoteModuleSettings } from '@views/screens/Quote/hooks';
import { Col, Divider, Form, Popconfirm, Row } from 'antd';
import { t } from 'i18next';

import { ENV_VARS } from '@/env';
import { FormSchema, useRegisterForm } from '@/modules/validators';
import HiddenFormInput from '@/views/components/Inputs/HiddenFormInput';

import Button from '../../../../../../components/Buttons/Button';
import FormInput from '../../../../../../components/Inputs/FormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../../../components/Labels/SecondaryLabel';
import TransportationModeSelector from '../../../../../../components/Selectors/TransportationModeSelector';
import HallTransportationDetailsCard from './HallTransportationDetailsCard';
import { additionalStopsValidation, initialValues, sequenceStopSorting } from './utils';

const isHalliburton = ENV_VARS.REACT_APP_THEME === 'halliburton';

const HallTransportationDetails = () => {
  const dispatch = useDispatch();
  const { getMilageByStops } = useSelector((state) => state.shipment);
  const [form] = Form.useForm();
  useRegisterForm(form, 'stopsForm');
  const formPorts = Form.useWatch('ports', form);
  const location = useLocation();
  const prepopulatedData = location.state?.quote;

  const { data = {} } = useSelector((state) => state.shipment.shipmentDetails);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);

  const { data: quoteModuleSettingsData } = useGetQuoteModuleSettings();
  const isMultiStopEnabled = quoteModuleSettingsData?.isMultyStopEnabled ?? false;
  const isAddressBookEnabled = quoteModuleSettingsData?.isAddressBookEnabled ?? false;

  useEffect(() => {
    if (copyQuoteKey) {
      form.setFields([
        { name: 'transportationMode', value: data.transportationMode },
        { name: 'consigneeAdditionalInformations', value: [{ distance: data.distance }] },
        {
          name: 'ports',
          value:
            data.transportationDetails?.sort(sequenceStopSorting)?.map((item) => ({
              addr1: item.address,
              addr2: item.addr2,
              city: item.portCity,
              companyName: item.companyName,
              countryCode: item.countryCode,
              editMode: true,
              name: item.portName,
              portRecKey: item.portRecKey,
              portState: item.portState,
              portType: item.portType,
              portZip: item.portZip,
              postalCode: item.postalCode,
              sequence: item.stopSequence,
              stopType: item.stopTypeId,
            })) || [],
        },
      ]);
    }
  }, [copyQuoteKey, data, form]);

  useEffect(() => {
    if (prepopulatedData) {
      const shipper = {
        city: prepopulatedData.shipperCity,
        companyName: prepopulatedData.shipperCompanyName,
        countryCode: prepopulatedData.shipperCountry,
        editMode: true,
        name: prepopulatedData.shipperPortCode,
        portRecKey: prepopulatedData.shipperPortRecKey,
        portState: prepopulatedData.shipperState,
        portType: prepopulatedData.shipperPortType,
        postalCode: prepopulatedData.shipperPostalCode,
        sequence: 1,
        stopType: '1',
      };

      const consignee = {
        city: prepopulatedData.consigneeCity,
        companyName: prepopulatedData.consigneeCompanyName,
        countryCode: prepopulatedData.consigneeCountry,
        editMode: true,
        name: prepopulatedData.consigneePortCode,
        portRecKey: prepopulatedData.consigneePortRecKey,
        portState: prepopulatedData.consigneeState,
        portType: prepopulatedData.consigneePortType,
        postalCode: prepopulatedData.consigneePostalCode,
        sequence: 2,
        stopType: '2',
      };

      form.setFieldsValue({
        ports: [shipper, consignee],
      });
    }
  }, [prepopulatedData, form]);

  const handleDataChange = (data, index) => {
    const portsLocal = form.getFieldValue('ports') || [];
    portsLocal[index] = { ...portsLocal[index], ...data };
    form.setFieldValue('ports', portsLocal);
  };

  const selectedStopsIndex =
    formPorts?.map(({ selected }, index) => (selected ? index : null)).filter((val) => val !== null) || [];

  const addStop = () => {
    const portsLocal = form.getFieldValue('ports') || [];
    const stopSequence = portsLocal.length + 1;

    portsLocal.push({
      city: null,
      companyName: null,
      countryCode: 'US',
      editMode: true,
      gridOperation: 1,
      isNew: true,
      name: null,
      portRecKey: null,
      portState: null,
      portType: null,
      postalCode: null,
      sequence: stopSequence,
      stopType: stopSequence === 1 ? '1' : null,
    });
    form.setFieldValue('ports', portsLocal);
  };

  const removeStop = (indexes) => {
    let portsLocal = form.getFieldValue('ports') || [];

    indexes
      .sort((a, b) => b - a)
      .forEach((index) => {
        if (index >= 0) {
          portsLocal.splice(index, 1);
        }
      });

    portsLocal = portsLocal.map((item, currentIndex) => ({
      ...item,
      sequence: currentIndex + 1,
      stopType: currentIndex === 0 ? '1' : item.stopType,
    }));

    form.setFieldValue('ports', portsLocal);
  };

  useEffect(() => {
    return () => {
      dispatch(getMilageByStopsInSuccess({ totalMilageInKm: 0, totalMilageInMiles: 0 }));
    };
  }, [dispatch]);

  return (
    <Form name="stopsForm" form={form} initialValues={initialValues}>
      <Row justify="space-between" align="middle">
        <Col flex={1}>
          <Row gutter={15} align="middle">
            <Col span={8}>
              <FormSchema.Field name="transportationMode">
                {({ antdRule, isRequired, name, label }) => (
                  <TransportationModeSelector
                    dataTestId="transportation-mode-field"
                    placeholder="Select transportation mode"
                    name={name}
                    label={label}
                    editMode
                    margin="m-0"
                    isRequired={isRequired}
                    rules={[antdRule]}
                    singleLineMode
                  />
                )}
              </FormSchema.Field>
            </Col>
            <Col data-testid="transportation-pick-up">
              <FormLabel>{t('translation:default.fields.pickUp')}</FormLabel>
              <br />
              <SecondaryLabel className="f-14 fw-600 text-dark">00</SecondaryLabel>
            </Col>
            <Col data-testid="transportation-delivery">
              <FormLabel>{t('translation:getQuote.transportationDetails.delivery')}</FormLabel>
              <br />
              <SecondaryLabel className="f-14 fw-600 text-dark">00</SecondaryLabel>
            </Col>
            <Col data-testid="transportation-total-distance-miles">
              <FormLabel>{t('translation:getQuote.transportationDetails.totalDistanceMiles')}</FormLabel>
              <br />
              <SecondaryLabel className="f-14 fw-600 text-dark">
                <NumericFormat
                  thousandSeparator
                  thousandsGroupStyle="thousand"
                  value={String(getMilageByStops?.totalMilageInMiles)}
                  displayType="text"
                  valueIsNumericString
                  decimalScale={2}
                  data-testid="total-distance-miles"
                />
              </SecondaryLabel>
            </Col>
            <Col data-testid="transportation-total-distance-km">
              <FormLabel>{t('translation:getQuote.transportationDetails.totalDistanceKm')}</FormLabel>
              <br />
              <SecondaryLabel className="f-14 fw-600 text-dark">
                <NumericFormat
                  thousandSeparator
                  thousandsGroupStyle="thousand"
                  value={String(getMilageByStops?.totalMilageInKm)}
                  displayType="text"
                  valueIsNumericString
                  decimalScale={2}
                  data-testid="total-distance-km"
                />
              </SecondaryLabel>
            </Col>
          </Row>
        </Col>
        <Col>
          <Row gutter={15}>
            <Col className="flex-1">
              <Form.List name="consigneeAdditionalInformations">
                {(consigneeAdditionalInformations) =>
                  consigneeAdditionalInformations.map(({ key }) => {
                    return (
                      <FormSchema.Field key={key} name={`consigneeAdditionalInformations.${key}.distance`}>
                        {({ antdRule, isRequired, label }) => {
                          return (
                            <FormInput
                              name={[key, 'distance']}
                              inputType="number"
                              label={label}
                              placeholder={t('translation:default.fields.distance.placeholder')}
                              margin="m-0"
                              isRequired={isRequired}
                              rules={[antdRule]}
                              singleLineMode
                              editMode
                              dataTestId="distance-field"
                            />
                          );
                        }}
                      </FormSchema.Field>
                    );
                  })
                }
              </Form.List>
            </Col>
            {isMultiStopEnabled && (
              <>
                <Col>
                  <Button
                    dataTestId="transportation-add-stop-button"
                    className={isHalliburton ? 'hb-button' : ''}
                    onClick={addStop}
                  >
                    {t('translation:getQuote.transportationDetails.buttons.addStop')}
                  </Button>
                </Col>
                <Col>
                  <Popconfirm
                    placement="topRight"
                    title="Are you sure you want to delete the stop?"
                    onConfirm={() => {
                      removeStop(selectedStopsIndex);
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      dataTestId="transportation-delete-stop-button"
                      variant="danger"
                      type="default"
                      disabled={selectedStopsIndex.length === 0}
                    >
                      {t('translation:getQuote.transportationDetails.buttons.deleteStop')}
                    </Button>
                  </Popconfirm>
                </Col>
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Form.List name="ports">
        {(fields) => (
          <div className="w-100">
            <Divider className="border-top-border my-15" style={{ marginLeft: '-10%', width: '120%' }} />
            <div>
              {fields.map(({ key, name, ...restField }, index) => (
                <HallTransportationDetailsCard
                  key={key}
                  index={index}
                  name={name}
                  stopDetails={formPorts?.[name]}
                  handleDataChange={handleDataChange}
                  isLast={fields.length === index + 1}
                  form={form}
                  totalStops={fields.length}
                  isMultiStopEnabled={isMultiStopEnabled}
                  isAddressBookEnabled={isAddressBookEnabled}
                  {...restField}
                />
              ))}
            </div>
          </div>
        )}
      </Form.List>
      <HiddenFormInput
        name="additionalStopsValidation"
        className="my-15"
        rules={additionalStopsValidation(form)}
        dataTestId="additional-stops-validation"
      />
    </Form>
  );
};

export default memo(HallTransportationDetails);
